import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';

export interface SearchResult {
  uuid: string;
  title: string;
  response: string;
  keywords: Array<string>;
  is_enabled: boolean;
  thumbnail: string;
  from: 'faq' | 'news' | 'article';
  type: string;
  category: string;
  created_at?: string;
  publication_date?: string;
}

interface RawSearchResult extends Omit<SearchResult, 'from'> {
  from?: string;
}

export interface SearchResponse {
  search_id: number;
  results: SearchResult[];
}

interface ArticleFaqSuggestionsResponse {
  results: RawSearchResult[];
}

interface RawSearchResponse {
  search_id: number;
  results: RawSearchResult[];
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  constructor(private httpClient: HttpClient) { }

  search(params: { query: string; filter?: 'faq' | 'news'; limit?: number; mode?: 'hybrid' | 'semantic' }): Observable<SearchResponse> {
    const url = `${environment.apiUrl}/typesense/search`;
    const httpParams: any = { query: params.query, filter: params.filter || 'faq' };
    if (typeof params.limit === 'number' && params.limit > 0) {
      httpParams.limit = String(params.limit);
    }
    if (params.mode) {
      httpParams.mode = params.mode;
    }
    return this.httpClient.get<RawSearchResponse>(url, { params: httpParams }).pipe(
      map((response) => ({
        search_id: response.search_id,
        results: (response.results || []).map((result) => this.normalizeSearchResult(result)),
      }))
    );
  }

  searchFaqSuggestionsByArticle(articleUuid: string, limit: number = 3): Observable<SearchResult[]> {
    const url = `${environment.apiUrl}/typesense/articles/${articleUuid}/faq-suggestions`;
    const params: any = {};
    if (typeof limit === 'number' && limit > 0) {
      params.limit = String(limit);
    }

    return this.httpClient.get<ArticleFaqSuggestionsResponse>(url, { params }).pipe(
      map((response) => (response?.results || []).map((result) => this.normalizeSearchResult(result)))
    );
  }

  autocomplete(query: string): Observable<Array<string>> {
    const url = `${environment.apiUrl}/typesense/autocomplete`;
    return this.httpClient.get<Array<string>>(url, {
      params: { query }
    });
  }

  getNotionSuggestions(query: string): Observable<Array<string>> {
    const url = `${environment.apiUrl}/typesense/notions?query=${encodeURIComponent(query)}`;
    return this.httpClient.get<Array<string>>(url);
  }

  click(searchId: number): Observable<any> {
    const url = `${environment.apiUrl}/typesense/${searchId}/click`;
    return this.httpClient.post(url, {});
  }

  private normalizeSearchResult(result: RawSearchResult): SearchResult {
    const source = typeof result?.from === 'string' ? result.from.toLowerCase() : '';
    const from: SearchResult['from'] = source === 'article' || source === 'news' ? source : 'faq';

    return {
      ...result,
      from,
    };
  }
}
